import type { ReactNode } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { useDragScroll } from './useDragScroll';
import { ReasoningBadge, reasoningTooltip } from './ReasoningBadge';
import { type PartItem } from './partsRun';
import { type ByStepText, type StepPart } from './stepPartsRun';
import { pieceNames, type InstrEntry, type SubStep, subStepLabel } from './stepsRun';
import { renderSubStepText } from './subStepText';
import { type StepBand } from './categoryBands';
import { useLanguage } from '../../contexts/LanguageContext';
import { useStableText } from '../../components/StableText';

// Everything about a step that is not a quantity: its diagram, the tools it calls for,
// the earlier steps it builds on, its printed instructions, and why the model said what it
// said. All of it used to live in a card under this table, which meant reading a step
// meant reading two things — so it lives in the step's own row instead.
export type StepDetail = {
  images?: string[];
  tools?: string[];
  buildsOn?: number[];
  instructions?: InstrEntry[];
  reason?: string;
  // Seconds the step's own call took. Beside the reasoning because the two are one answer to
  // "what happened when this step was asked", and every panel that opens the one wants the
  // other — see reasoningTooltip, which draws it at the top.
  took?: number;
};

// Strip a leading language tag the transcription sometimes prints on each line ("FR/ ",
// "ESP/ ", "ENG/ "…). Only a run of 2–4 uppercase letters before the slash is taken, so a
// real sentence isn't clipped.
export function stripLangPrefix(text: string): string {
  return text
    .split('\n')
    .map((line) => line.replace(/^\s*[A-Z]{2,4}\s*\/\s*/, ''))
    .join('\n');
}

// Does the total quantity of each part used across all steps equal its supplied count?
// Only parts with a known supplied quantity are checked; a conclusion banner sits over a
// per-part table (mismatches highlighted).
//
// One table, shown in both phases that have something to add to it — the same columns, the
// same parts, one extra band of rows each:
//   `steps` gives the repartition ITSELF: one row per assembly step, above the totals it
//   produces. That is where the numbers are decided, so that is where they are read.
//   `subSteps` opens each step into the gestures it is made of, one row each.
//
// Given both, the table reads top to bottom as one nesting: supplied, shared out among the
// steps, each step shared out among its gestures.
//
// WHETHER EACH BAND ADDS UP TO THE ONE ABOVE IT IS NOW A QUESTION, not a guarantee. The
// server used to force the sub-step half by rewriting the quantities, so the arithmetic on
// screen was true by construction; it stores the model's answer as given now, and the
// banner below checks the sum instead. Which is the point of drawing the band at all.
export function Reconciliation({
  items,
  itemCrops,
  used,
  subSteps,
  steps,
  stepText,
  detail,
  onZoom,
  busy = false,
  running,
  // Hidden when the card is a page of the step navigator, which already titles it.
  showHeader = true,
  verdictOnly = false,
  allocBusy = false,
  printedBusy = false,
  children,
}: {
  items: PartItem[];
  itemCrops: (string | null)[];
  used: Record<string, StepPart[]>;
  // Every step's sub-steps, keyed by step category like `used`. Omitted where there are
  // none to read.
  subSteps?: Record<string, SubStep[]>;
  // The assembly steps, in printed order. Given, each becomes a row of the table — the
  // repartition itself, rather than only what it totals to.
  steps?: StepBand[];
  // What each step achieves, from the same call that allocated the parts. Printed on the
  // step's row: it is the reason the row says what it says, and a row of numbers with no
  // account of itself is the one thing this table was meant to stop being.
  stepText?: ByStepText;
  // The rest of what Phase 3 knows about each step. Given, the step rows carry it and the
  // TOOLS get columns of their own — tools are not reconciled (they are used, never used
  // up), so they sit past the parts behind a divider, marked with a dot per step and
  // sub-step that calls for them.
  detail?: Record<string, StepDetail>;
  // Open a step's diagram zoomed; the lightbox belongs to the parent.
  onZoom?: (src: string) => void;
  // A RUN IS UNDER WAY, and which steps it is inside of right now (by category).
  //
  // Without these the table goes quiet for the length of a run and looks settled: a fresh run
  // wipes the sub-steps, so the rows that would be moving are simply absent, and the banner
  // falls back to the step-total claim — a green tick over a table being rebuilt underneath
  // it. A step with no sub-steps is then "not analysed", "being read", or "waiting its turn",
  // and only the run knows which.
  busy?: boolean;
  running?: string[];
  showHeader?: boolean;
  // THE CONCLUSION WITHOUT THE TABLE UNDER IT. Phase 4 shows one sub-step at a time now, so
  // a matrix of every part against every gesture is the one thing on that page not about the
  // sub-step in front of you — but the verdict still is: it is a statement about the manual,
  // and it is how you learn there is something to go and look for. The check itself is
  // unchanged and still runs over everything; only the drawing of it stops here.
  verdictOnly?: boolean;
  // THE CALLS BEHIND THE CELLS ARE OUT AGAIN: the repartition (the counts, the step's line, its
  // tools) and the transcription (the printed text). The table keeps its shape and each cell
  // one of them answers spins, rather than the last run's answer sitting there as this one's —
  // or the whole table vanishing under a spinner, which is what it did.
  allocBusy?: boolean;
  printedBusy?: boolean;
  // WHAT THE VERDICT IS ABOUT, when it is not this table: drawn under the verdict, in the same
  // card — Phase 5's sub-step table, with its conclusion on top as Phase 4 has its own.
  children?: ReactNode;
}) {
  const { t, language } = useLanguage();
  const st = useStableText();
  const scrollRef = useDragScroll<HTMLDivElement>();
  // The pieces by id, for the sub-step sentences below: each names its parts by id in braces
  // and is drawn out of them (see subStepText).
  const pieceName = pieceNames(steps ?? [], subSteps ?? {}, language);

  // Sum each part's used quantity across every step.
  const usedTotal = new Map<number, number>();
  let usedQtyUnknown = false;
  for (const cat of Object.keys(used)) {
    for (const u of used[cat]) {
      const i = Number(u.id);
      if (u.qty == null) usedQtyUnknown = true;
      usedTotal.set(i, (usedTotal.get(i) ?? 0) + (u.qty ?? 0));
    }
  }


  // Tools are reused, not consumed, so they aren't reconciled against a supplied
  // count; only real parts with a known supplied quantity are checked.
  const rows = items
    .map((p, i) => ({ p, i, supplied: p.qty, used: usedTotal.get(i) ?? 0 }))
    .filter(
      (r): r is { p: PartItem; i: number; supplied: number; used: number } =>
        r.supplied != null && !r.p.tool,
    );
  const skipped = items.filter((p) => !p.tool && p.qty == null).length;
  if (rows.length === 0) return null;

  const mismatches = rows.filter((r) => r.used !== r.supplied);
  const ok = mismatches.length === 0;

  // THE SUB-STEP HALF, which is this phase's own claim rather than the one above it.
  //
  // The banner used to say "every supplied part is used exactly as many times as provided"
  // in both phases, and that sentence is about the STEP totals against the supplied counts
  // — Phase 3's numbers. It stood for the whole table only because the server used to force
  // the sub-steps to partition those totals, which made the upper claim imply the lower one.
  //
  // That partition is gone: the sub-step quantities are the model's answer as given. So the
  // implication went with it, and this is the check that was standing in for it — without
  // which the sentence would go on being true about Phase 3 while wrong sub-step numbers sat
  // directly beneath it. A green tick over a red table.
  //
  // A step nobody has analysed is not counted. It has no sub-steps to disagree with its
  // total, and reading that as a failure would flag every step before the run reaches it.
  const subFails: { step: string; part: number; want: number; got: number }[] = [];
  let subChecked = 0;
  for (const cat of Object.keys(subSteps ?? {})) {
    const subs = subSteps?.[cat] ?? [];
    if (!subs.length || !used[cat]) continue;
    subChecked++;
    for (const u of used[cat]) {
      // An unknown step total cannot be partitioned, so there is nothing to check against.
      if (u.qty == null) continue;
      const i = Number(u.id);
      let got = 0;
      for (const s of subs) {
        for (const p of s.parts ?? []) {
          // A part merely HANDLED is outside the count — see _parse_used. The passes of a
          // passes of a cycle used to multiply this and a part marked `once` escaped them;
          // a gesture is one gesture now, so a quantity is a quantity.
          if (Number(p.id) !== i || p.consumed === false) continue;
          got += p.qty ?? 0;
        }
      }
      if (got !== u.qty) subFails.push({ step: cat, part: i, want: u.qty, got });
    }
  }
  // WHICH ONES, in the manual's own naming. A count alone — "1 part does not add up" — is a
  // sentence you cannot act on over a table several screens wide: it says something is wrong
  // somewhere in twelve steps and thirty gestures, and finding it is arithmetic done by hand.
  //
  // THE STEP AND THE PART, and deliberately not a sub-step. The failure is that a step's
  // sub-steps do not TOTAL its own quantity, which is a property of the set: three gestures
  // claiming one screw each against a step that takes four is a shortfall belonging to all
  // three, and naming any one of them would be inventing a culprit. The named step and part
  // are exactly as precise as the check is.
  const numOf = new Map((steps ?? []).map((s) => [s.category, s.num]));
  const labelOf = (i: number) => {
    const p = items[i];
    if (!p) return `#${i}`;
    const name = p.nameFr || p.nameEn || p.name || `#${i}`;
    return p.letter ? `${name} (${p.letter})` : name;
  };
  // The same failures as a lookup, so the cells they are about can carry the colour too.
  const failedCells = new Set(subFails.map((f) => `${f.step}#${f.part}`));

  // Only where there are sub-steps to speak about. Phase 3 draws this table without them
  // and keeps the claim it can make.
  const subMode = subChecked > 0;
  const subOk = subFails.length === 0;
  // NOT ASKED, as against asked and answered "nothing": a step whose analysis came back empty — the
  // finished chair standing on its legs — has an empty list stored, and is not waiting on anything.
  const unanalysed = (steps ?? []).filter((s) => !(s.category in (subSteps ?? {}))).length;

  // WHY A STEP HAS NO SUB-STEP ROWS. Three different reasons, and the row says which:
  // its call is in flight, it is queued behind the four that are, or nobody has asked. The
  // last one is silent — that is the table at rest, and the banner already counts it.
  const inFlight = new Set(running ?? []);
  const stepStatus = (category: string): 'running' | 'queued' | null => {
    if ((subSteps?.[category] ?? []).length) return null;
    if (inFlight.has(category)) return 'running';
    return busy ? 'queued' : null;
  };

  // THE GRID IS DRAWN AT ONE WEIGHT, inner and outer alike. It was a hairline inside
  // (slate-100, and nothing at all between rows) around a slate-300 frame, which is the
  // shape that reads as one box with some text in it — and this is a MATRIX. What it is
  // for is holding a row of numbers against a column of parts across several screens of
  // scrolling, and the lines are the only thing carrying the eye along either.
  const RULE = 'border-slate-300';
  // The sticky column's right edge is a shadow rather than a border, because a border on a
  // sticky cell scrolls out from under itself. Same two colours as the rule, by hand:
  // slate-300 and slate-600.
  const stickyCol =
    'sticky left-0 z-10 bg-white shadow-[inset_-2px_0_0_0_#cbd5e1]';
  const reconLabelCls = `${stickyCol} ${RULE} border-b px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-500`;
  // THE LABEL COLUMN NAMES THE ROW AND NOTHING ELSE — "STEP 1", "1A" — and stays put while the
  // parts scroll under it. What the step SAYS is a column of its own beside it (descCls): it is
  // an answer, and in the header column it read as the question the row was asked.
  const LABEL_W = 'min-w-[6rem] max-w-[6rem]';
  const stepLabelCls = `${stickyCol} ${RULE} ${LABEL_W} border-b px-3 py-2 text-center align-middle`;
  const subLabelCls = `${stickyCol} ${RULE} ${LABEL_W} border-b px-3 py-2 text-center align-middle font-normal normal-case`;
  // The description: a min-width, because `max-w` alone is a cap on nothing — a table lays its
  // columns out as narrow as their content allows, and a sentence that wraps allows almost
  // anything.
  const descCls = `min-w-[20rem] max-w-[20rem] ${RULE} border-b border-r px-3 py-2 text-center align-middle`;
  // NARROW, because a cell holds a number. The columns were a sentence wide for the part's
  // description in the header, which made twelve parts several screens across for numbers
  // meant to be compared along a row; the description is the header's tooltip now.
  // `last:border-r-0` and not `last:border-0`, which would take the row's line with it and
  // leave every rightmost cell missing its underside.
  const reconCellCls =
    `min-w-[5.5rem] max-w-[5.5rem] ${RULE} border-b border-r px-2 py-2 text-center last:border-r-0`;
  // The tools, past the parts, behind a heavier rule. They belong in the table — a step
  // calls for a screwdriver as surely as it calls for a screw — but not in its arithmetic:
  // a tool is used and handed back, so it has nothing to total and nothing to reconcile.
  // The divider says which side of that line a column is on.
  // Only where a step has been asked its tools: a manual allocated before they were asked for
  // does not grow a band of empty columns.
  const toolCols = detail && Object.values(detail).some((d) => d.tools)
    ? items.map((p, i) => ({ p, i })).filter(({ p }) => p.tool) : [];
  // WHAT EACH STEP PRINTS, verbatim, once the transcription has answered — a column of its own
  // beside the description: the manual's words beside the model's.
  const printed = !!detail && Object.values(detail).some((d) => d.instructions);
  // IN THE APP'S LANGUAGE ONLY: the manual prints every step in three or four, and the other
  // translations of the same sentence were a column three times as tall saying one thing.
  const printedOf = (entries: InstrEntry[] | undefined) =>
    (entries ?? []).filter((e) => e.lang === language);
  const toolCellCls = (first: boolean) =>
    `${reconCellCls}${first ? ' border-l-2 border-l-slate-300' : ''}`;
  // "This step uses it", with nothing to count. Filled, where a part merely handled is a
  // hollow ring: the tool IS used here, it is simply never used up.
  const toolMark = (
    <span className="inline-block h-2 w-2 rounded-full bg-slate-400" />
  );
  const spin = <Loader2 className="mx-auto h-4 w-4 animate-spin text-slate-400" />;
  const noMark = <span className="text-slate-200">—</span>;

  // What one sub-step does to one part: the units it uses up, or "handled" for a part it
  // only touches — laid flat, turned over, or held while the new part is fixed to it.
  // One gesture, so the number shown is the quantity it names — which is what adds up to
  // the step row above.
  const subStepCell = (sub: SubStep, partIdx: number): { qty: number; handled: boolean } | null => {
    const entry = (sub.parts ?? []).find((p) => Number(p.id) === partIdx);
    if (!entry) return null;
    if (entry.consumed === false) return { qty: 0, handled: true };
    return { qty: entry.qty ?? 0, handled: false };
  };

  // Is any part merely handled anywhere? The legend for that mark is only worth printing
  // when the mark is on screen.
  const anyHandled = Object.values(subSteps ?? {}).some((subs) =>
    (subs ?? []).some((s) => (s.parts ?? []).some((p) => p.consumed === false)),
  );

  return (
    // Wider than the cards around it, and deliberately: this one is a matrix, and the
    // reader is trying to hold a row of numbers against a column of parts. Every rem is a
    // part they do not have to scroll for — and it now carries a sentence per step too.
    <div className="mx-auto w-full max-w-6xl">
      <div className="overflow-hidden rounded-xl border-2 border-slate-300 bg-white shadow-sm">
        {showHeader && (
          <div className="border-b-2 border-slate-300 bg-slate-50 px-4 py-2 text-center">
            <h4 className="text-sm font-bold tracking-tight text-slate-900">
              {t('notice.steps.reconTitle')}
            </h4>
          </div>
        )}

        <div className="flex flex-col gap-3 p-4">
          {/* Conclusion — this table's own, which depends on which rows it is drawing.
              With sub-steps it is about them; without, about the steps.

              NO CONCLUSION WHILE THE RUN IS ON. Half the steps have answered and half have
              not, so every claim here is about a table that is still being written — and the
              one it fell back to was Phase 3's, a green tick with nothing to do with the work
              in flight beneath it. */}
          {busy || allocBusy ? (
            <p className="flex items-center justify-center gap-2 text-center text-sm font-semibold text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('notice.steps.reconRunning')}
            </p>
          ) : subMode ? (
            <div className="flex flex-col gap-1">
              {subOk ? (
                <p className="flex items-center justify-center gap-2 text-center text-sm font-semibold text-blue-600">
                  <Check className="h-4 w-4" />
                  {t('notice.steps.reconSubOk')}
                </p>
              ) : (
                <div className="text-center text-amber-600">
                  <p className="text-sm font-semibold">
                    {subFails.length} {t('notice.steps.reconSubBad')}
                  </p>
                  <ul className="mt-0.5 text-xs">
                    {subFails.map((f) => (
                      <li key={`${f.step}#${f.part}`}>
                        {t('notice.parts.stepRow')} {numOf.get(f.step) ?? '?'} · {labelOf(f.part)}
                        {' — '}
                        {t('notice.steps.reconSubBadLine')
                          .replace('{got}', String(f.got))
                          .replace('{want}', String(f.want))}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* The step totals are Phase 3's answer and this phase reads them. When they
                  are wrong, saying only that the sub-steps add up would be true and
                  useless: they add up to a total that does not. */}
              {!ok && (
                <p className="text-center text-xs text-amber-600">
                  {mismatches.length} {t('notice.steps.reconBad')}
                </p>
              )}
              {unanalysed > 0 && (
                <p className="text-center text-xs text-slate-400">
                  {t('notice.steps.reconSubPending').replace('{n}', String(unanalysed))}
                </p>
              )}
            </div>
          ) : ok ? (
            <p className="flex items-center justify-center gap-2 text-center text-sm font-semibold text-blue-600">
              <Check className="h-4 w-4" />
              {t('notice.steps.reconOk')}
            </p>
          ) : (
            <p className="text-center text-sm font-semibold text-amber-600">
              {mismatches.length} {t('notice.steps.reconBad')}
            </p>
          )}

          {verdictOnly && children}
          {!verdictOnly && (
            <>
          {/* Per-part totals, transposed: one column per part, scrolling
              horizontally. The sticky first column holds the row labels. */}
          {/* No horizontal padding on the scroller, and none faked with a negative margin
              either. A sticky column sticks to the scrollport's PADDING edge, so `px-4`
              here parked it 1rem inside the box and left a 1rem strip at the left where
              the scrolled-away columns showed through — a slice of some part's drawing and
              a stray cell border, sliding along beside the labels that were supposed to be
              covering them. The card's own p-4 gives the spacing; this box just scrolls. */}
          {/* FRAMED ALL ROUND. The cells draw only their bottom and right rules, so the table's
              top, left and — when it ends short of the box — right edges were no line at all.
              The frame is the outer border; `min-w-full` stretches the table to meet it. */}
          <div ref={scrollRef} className={`cursor-grab overflow-x-auto rounded-lg border ${RULE}`}>
            <table className="min-w-full text-sm">
              <tbody className="[&>tr:last-child>*]:border-b-0">
                <tr>
                  <th className={reconLabelCls} />
                  <th className={`${descCls} align-bottom text-xs font-semibold uppercase tracking-wide text-slate-500`}>
                    {st('notice.steps.reconDescription')}
                  </th>
                  {printed && (
                    <th className={`${descCls} align-bottom text-xs font-semibold uppercase tracking-wide text-slate-500`}>
                      {st('notice.steps.reconPrinted')}
                    </th>
                  )}
                  {[...rows.map((r) => ({ ...r, tool: false, first: false })),
                    ...toolCols.map((c, k) => ({ p: c.p, i: c.i, tool: true, first: k === 0 }))].map((r) => {
                    const label = r.p.letter || r.p.ref || r.p.name || '?';
                    const crop = itemCrops[r.i];
                    return (
                      <td
                        key={r.i}
                        className={`${r.tool ? toolCellCls(r.first) : reconCellCls} py-2`}
                        // What the part is — Phase 2's answer, borrowed, and half of what the
                        // video prompt says about it: on hover, where it costs no width.
                        title={r.p.description || undefined}
                      >
                        <div className="flex flex-col items-center gap-1">
                          <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded border border-slate-200 bg-white">
                            {crop ? (
                              <img src={crop} alt={label} className="max-h-full max-w-full object-contain" />
                            ) : (
                              <span className="text-slate-300">…</span>
                            )}
                          </div>
                          <span className="text-xs font-semibold text-slate-700">
                            {label}
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
                {/* USED AGAINST SUPPLIED, IN ONE CELL. They were a row each, top and
                    bottom of a table several screens tall — and the whole question this
                    table answers is whether the two match, which is a comparison nobody can
                    make between two numbers they cannot see at once. Written as a fraction
                    it is answered at a glance, per part, and the row it takes the place of
                    was the only reason to scroll to the end.

                    THE WHOLE CELL TAKES THE COLOUR, not the used half alone. The claim is
                    about the PAIR — these two numbers agree, or they do not — and colouring
                    one side of it made the fraction read as two facts of unequal standing,
                    the green one asserting something and the grey one merely present. */}
                <tr>
                  <th className={reconLabelCls}>{st('notice.steps.reconUsedSupplied')}</th>
                  <td className={descCls} />
                  {printed && <td className={descCls} />}
                  {rows.map((r) => {
                    const match = r.used === r.supplied;
                    return (
                      <td
                        key={r.i}
                        className={`${reconCellCls} font-semibold tabular-nums ${
                          match
                            ? 'text-blue-600'
                            : 'text-amber-600'
                        }`}
                      >
                        {allocBusy ? spin : (
                          <>
                            {r.used} / {r.supplied}
                            {!match && (
                              <span className="ml-1 text-xs">
                                ({r.used > r.supplied ? '+' : ''}
                                {r.used - r.supplied})
                              </span>
                            )}
                          </>
                        )}
                      </td>
                    );
                  })}
                  {/* A tool has nothing to reconcile — it is used and handed back — so its
                      column shows the supplied count alone rather than a fraction over it. */}
                  {toolCols.map((c, k) => (
                    <td
                      key={c.i}
                      className={`${toolCellCls(k === 0)} tabular-nums text-slate-700`}
                    >
                      {c.p.qty ?? noMark}
                    </td>
                  ))}
                </tr>
                {/* The repartition itself: one row per step, each cell what that step
                    takes. A step that takes none of a part shows a dash — a column of
                    zeros would weigh as much on the eye as the numbers that matter, and
                    most parts belong to one step. */}
                {(steps ?? []).map((s) => {
                  const byId = new Map((used[s.category] ?? []).map((u) => [Number(u.id), u]));
                  const status = stepStatus(s.category);
                  const d = detail?.[s.category];
                  const stepTools = new Set(d?.tools ?? []);
                  // WHAT THE CALL ACTUALLY RETURNED FOR THIS STEP, under the reasoning that
                  // produced it. The table draws a reading of that answer — quantities,
                  // marks, sentences — and every field it does not draw is invisible, which
                  // is most of them: the per-sub-step `fitting` Phase 5 fills its selectors
                  // from is answered here and shown nowhere. So the badge shows the object.
                  const stepRaw = JSON.stringify({
                    tools: d?.tools ?? [],
                    buildsOn: d?.buildsOn ?? [],
                    instructions: d?.instructions ?? [],
                    subSteps: subSteps?.[s.category] ?? [],
                  }, null, 2);
                  return [
                    <tr key={s.category}>
                      <th className={stepLabelCls}>
                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          {st('notice.parts.stepRow')} {s.num}
                        </span>
                        {(d?.reason || (subSteps?.[s.category] ?? []).length > 0) && (
                          <span className="ml-1.5 inline-block align-middle">
                            <ReasoningBadge
                              content={reasoningTooltip(d?.reason ?? '', stepRaw, t, d?.took ?? null)}
                              label={t('notice.cat.thinking')}
                              reasons
                            />
                          </span>
                        )}
                      </th>
                      <td className={descCls}>
                        {/* The step's own diagram, straight from the PDF. It reads first
                            because it is what the row is ABOUT: the numbers to its right
                            are claims about this picture. */}
                        {(d?.images ?? []).map((src, k) => (
                          <button
                            key={k}
                            type="button"
                            onClick={() => onZoom?.(src)}
                            className="mb-1.5 block w-full cursor-zoom-in overflow-hidden rounded border border-slate-200 bg-white transition-opacity hover:opacity-90"
                          >
                            <img src={src} alt="" className="w-full" />
                          </button>
                        ))}
                        {allocBusy ? spin : stepText?.[s.category] && (
                          <span className="block whitespace-normal break-words text-xs leading-snug text-slate-600">
                            {(language === 'fr'
                              ? stepText[s.category].fr
                              : stepText[s.category].en) || stepText[s.category].en}
                          </span>
                        )}
                        {/* What it is built ON, by step number — the earlier sub-assemblies
                            this one attaches to. The numbers are the rows above. */}
                        {!!d?.buildsOn?.length && (
                          <span className="mt-1 block text-[11px] text-slate-400">
                            {st('notice.steps.reused')}: {d.buildsOn.join(', ')}
                          </span>
                        )}
                      </td>
                      {/* The manual's own words for this step, transcribed verbatim, in the
                          reader's language. Italic, so they are never mistaken for something
                          the model wrote. */}
                      {printed && (
                        <td className={descCls}>
                          {/* Nothing where a step prints nothing: an empty cell says it. */}
                          {printedBusy ? spin : printedOf(d?.instructions).map((e, k) => (
                            <span key={k} className="mb-1 block whitespace-pre-line break-words text-xs italic leading-snug text-slate-600 last:mb-0">
                              {stripLangPrefix(e.text)}
                            </span>
                          ))}
                        </td>
                      )}
                      {rows.map((r) => {
                        const u = byId.get(r.i);
                        // The step total its own sub-steps failed to reach, coloured where
                        // the disagreement is — the sentence above names this cell, and in a
                        // table this wide a name you still have to scroll for is half a name.
                        const failed = failedCells.has(`${s.category}#${r.i}`);
                        return (
                          <td
                            key={r.i}
                            className={`${reconCellCls} tabular-nums ${
                              failed
                                ? 'font-semibold text-amber-600'
                                : 'text-slate-700'
                            }`}
                          >
                            {allocBusy ? spin : u?.qty == null ? (
                              <span className="text-slate-300">
                                {u ? '?' : '—'}
                              </span>
                            ) : (
                              u.qty
                            )}
                          </td>
                        );
                      })}
                      {toolCols.map((c, k) => (
                        <td key={c.i} className={toolCellCls(k === 0)}>
                          {allocBusy ? spin : stepTools.has(String(c.i)) ? toolMark : noMark}
                        </td>
                      ))}
                    </tr>,
                    // A step whose gestures are being read, or waiting to be: ONE row where
                    // they will be, so the table keeps the shape it is about to have and the
                    // work is visible in the place it will land. A run that only spun its
                    // button left this whole band looking finished.
                    ...(status ? [(
                      <tr key={`${s.category}-pending`} className="align-top">
                        <th className={subLabelCls}>
                          <span className="flex items-center justify-center gap-2 text-xs font-normal normal-case text-slate-400">
                            {status === 'running' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                            {st(status === 'running'
                              ? 'notice.steps.subRunning'
                              : 'notice.steps.subQueued')}
                          </span>
                        </th>
                        <td className={descCls} />
                        {printed && <td className={descCls} />}
                        {rows.map((r) => (
                          <td key={r.i} className={reconCellCls}>{noMark}</td>
                        ))}
                        {toolCols.map((c, k) => (
                          <td key={c.i} className={toolCellCls(k === 0)}>{noMark}</td>
                        ))}
                      </tr>
                    )] : []),
                    // …and, under it, the gestures that step is made of. Indented and in
                    // lighter type: they are a breakdown of the row above, not five more
                    // rows of the same kind, and they must not compete with it.
                    ...(subSteps?.[s.category] ?? []).map((sub) => {
                      return (
                        <tr key={`${s.category}-${sub.letter}`} className="align-top">
                          <th className={subLabelCls}>
                            <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                              {subStepLabel(s.num, sub.letter)}
                            </span>
                            {/* This gesture's own object, whole. The reasoning above it is
                                the STEP's — one call answers the whole step, so there is no
                                finer thinking to show — but the output half is this
                                sub-step's alone, which is where its parts, its tools, its
                                holes and its fitting are readable. */}
                            <span className="ml-1.5 inline-block align-middle">
                              <ReasoningBadge
                                content={reasoningTooltip(
                                  d?.reason ?? '',
                                  JSON.stringify(sub, null, 2),
                                  t,
                                )}
                                label={t('notice.cat.thinking')}
                                reasons
                              />
                            </span>
                          </th>
                          <td className={descCls}>
                            <span className="block whitespace-normal break-words text-xs leading-snug text-slate-500">
                              {renderSubStepText(
                                (language === 'fr' ? sub.textFr : sub.textEn) || sub.textEn,
                                sub.parts,
                                items,
                                pieceName,
                                language,
                              )}
                            </span>
                          </td>
                          {printed && <td className={descCls} />}
                          {rows.map((r) => {
                            const cell = subStepCell(sub, r.i);
                            return (
                              <td
                                key={r.i}
                                className={`${reconCellCls} tabular-nums text-slate-500`}
                              >
                                {!cell ? (
                                  <span className="text-slate-200">—</span>
                                ) : cell.handled ? (
                                  // Present in the action, used up by another: no quantity
                                  // to give, and a zero here would read as "none of it".
                                  <span
                                    title={t('notice.steps.subHandled')}
                                    className="inline-block h-2 w-2 rounded-full border border-slate-400"
                                  />
                                ) : (
                                  cell.qty
                                )}
                              </td>
                            );
                          })}
                          {toolCols.map((c, k) => (
                            <td key={c.i} className={toolCellCls(k === 0)}>
                              {(sub.tools ?? []).includes(String(c.i)) ? toolMark : noMark}
                            </td>
                          ))}
                        </tr>
                      );
                    }),
                  ];
                })}
              </tbody>
            </table>
          </div>

          {/* What a mark means, then the caveats that weaken the conclusion. */}
          {(toolCols.length > 0 || anyHandled || skipped > 0 || usedQtyUnknown) && (
            <div className="text-center text-xs text-slate-400">
              {toolCols.length > 0 && (
                <p className="flex items-center justify-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full bg-slate-400" />
                  {t('notice.steps.toolLegend')}
                </p>
              )}
              {anyHandled && (
                <p className="flex items-center justify-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full border border-slate-400" />
                  {t('notice.steps.subHandledLegend')}
                </p>
              )}
              {skipped > 0 && <p>{skipped} {t('notice.steps.reconSkipped')}</p>}
              {usedQtyUnknown && <p>{t('notice.steps.reconUnknownQty')}</p>}
            </div>
          )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
